<template>
  <section
    class="relative bg-gradient-to-b from-white to-gray-50 overflow-hidden"
  >
    <!-- Decorative background elements -->
    <div class="absolute inset-0 z-0 opacity-10">
      <div
        class="absolute top-20 left-10 w-24 h-24 rounded-full bg-primary-400 animate-pulse-slow"
      ></div>
      <div
        class="absolute bottom-20 right-10 w-32 h-32 rounded-full bg-secondary-400 animate-pulse-slow"
        style="animation-delay: 1s"
      ></div>
      <div
        class="absolute top-40 right-1/4 w-16 h-16 rounded-full bg-yellow-400 animate-pulse-slow"
        style="animation-delay: 0.5s"
      ></div>

      <!-- Musical notes scattered around -->
      <svg
        class="absolute top-1/4 left-1/3 h-8 w-8 text-primary-300 animate-bounce-slow"
        style="animation-delay: 0.2s"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 20 20"
        fill="currentColor"
      >
        <path
          d="M18 3a1 1 0 00-1.196-.98l-10 2A1 1 0 006 5v9.114A4.369 4.369 0 005 14c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V7.82l8-1.6v5.894A4.37 4.37 0 0015 12c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V3z"
        />
      </svg>

      <svg
        class="absolute bottom-1/3 right-1/3 h-6 w-6 text-secondary-300 animate-bounce-slow"
        style="animation-delay: 1.2s"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 20 20"
        fill="currentColor"
      >
        <path
          d="M18 3a1 1 0 00-1.196-.98l-10 2A1 1 0 006 5v9.114A4.369 4.369 0 005 14c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V7.82l8-1.6v5.894A4.37 4.37 0 0015 12c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V3z"
        />
      </svg>

      <svg
        class="absolute top-2/3 left-1/4 h-10 w-10 text-primary-200 animate-bounce-slow"
        style="animation-delay: 0.7s"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 20 20"
        fill="currentColor"
      >
        <path
          d="M18 3a1 1 0 00-1.196-.98l-10 2A1 1 0 006 5v9.114A4.369 4.369 0 005 14c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V7.82l8-1.6v5.894A4.37 4.37 0 0015 12c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V3z"
        />
      </svg>
    </div>

    <div class="container-lg section-padding relative z-10">
      <div class="grid md:grid-cols-2 gap-12 items-center">
        <div class="slide-in-bottom show">
          <h1
            class="text-4xl sm:text-5xl font-extrabold tracking-tight text-gray-900 mb-6 leading-tight"
            data-testid="hero-title"
          >
            Find a music teacher in
            <span class="text-primary-800">{{ region }}</span>
          </h1>
          <p class="text-xl text-gray-500 mb-8 max-w-2xl">
            Book lessons, pay by card, and keep everything in one place.
            Crescendo is new — we're building it with a small group of teachers
            across Thanet, Canterbury and the towns around them.
          </p>
          <div class="flex flex-wrap gap-4">
            <router-link to="/teachers" class="btn-primary btn-lg group">
              <span class="group-hover:mr-2 transition-all duration-300"
                >Browse teachers</span
              >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M17 8l4 4m0 0l-4 4m4-4H3"
                />
              </svg>
            </router-link>
            <router-link to="/for-teachers" class="btn-secondary btn-lg group">
              <span class="mr-2">Teach with Crescendo</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M17 8l4 4m0 0l-4 4m4-4H3"
                />
              </svg>
            </router-link>
          </div>
        </div>
        <div class="pop-in show">
          <div class="relative overflow-hidden">
            <!-- Main image with shadow and hover effect -->
            <div
              class="rounded-lg overflow-hidden shadow-elevation-3 transition-all duration-500 hover:shadow-elevation-4 transform hover:-translate-y-2"
            >
              <img
                class="w-full h-auto object-cover"
                src="https://images.unsplash.com/photo-1516280440614-37939bbacd81?ixlib=rb-1.2.1&auto=format&fit=crop&w=1650&q=80"
                alt="Music teacher and student"
              />
            </div>

            <!-- Decorative elements -->
            <div
              class="absolute -bottom-6 -left-6 w-24 h-24 bg-primary-100 rounded-lg -z-10 transform rotate-6"
            ></div>
            <div
              class="absolute -top-6 -right-6 w-24 h-24 bg-secondary-100 rounded-lg -z-10 transform -rotate-6"
            ></div>

            <!-- Floating stats cards sit inside the image box, not overhanging
                 it: the wrapper above is overflow-hidden, so the old -right-6 /
                 -left-6 offsets were clipped at every width. The short numeric
                 values these used to hold were narrow enough not to show it. -->
            <div
              class="hidden sm:block absolute bottom-4 right-2 bg-white rounded-lg shadow-elevation-2 p-4 max-w-xs animate-pulse-slow"
            >
              <div class="flex items-center">
                <div class="rounded-full bg-primary-100 p-2 mr-3">
                  <svg
                    class="h-6 w-6 text-primary-600"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    />
                  </svg>
                </div>
                <div>
                  <p class="text-xs text-gray-500">Card payments</p>
                  <p class="text-sm font-semibold text-gray-900 leading-snug">
                    Secured by Stripe
                  </p>
                </div>
              </div>
            </div>

            <!-- Floating stats card 2 -->
            <div
              class="hidden sm:block absolute top-4 left-2 bg-white rounded-lg shadow-elevation-2 p-4 max-w-xs animate-pulse-slow"
              style="animation-delay: 1.5s"
            >
              <div class="flex items-center">
                <div class="rounded-full bg-secondary-100 p-2 mr-3">
                  <svg
                    class="h-6 w-6 text-secondary-600"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z"
                    />
                  </svg>
                </div>
                <div>
                  <p class="text-xs text-gray-500">Cancellations</p>
                  <p class="text-sm font-semibold text-gray-900 leading-snug">
                    Handled in the app
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { LAUNCH } from "@/config/launch";

export default {
  name: "HeroSection",
  setup() {
    return {
      region: LAUNCH.region,
    };
  },
};
</script>
